<template>
  <!-- Need to stop touchmove event propagation, otherwise it is broken -->
  <div
    class="no-scrollbar flex touch-pan-x justify-between space-x-1 overflow-x-auto overflow-y-hidden"
    @scroll="changeActiveColumnByScroll"
    @touchmove.stop
  >
    <div class="w-1/2 shrink-0"></div>
    <GraphColumn
      v-for="search in searches"
      :key="search.id"
      ref="columnRef"
      :class="{ [`activeColumn${direction}`]: search.isActive }"
      :search="search"
      :direction="direction"
      :column-height="columnLib.getColumnHeight(maxPrice, search.price)"
      @onClick="changeActiveColumnByClick"
    />
    <div class="w-1/2 shrink-0"></div>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, ref, watch } from 'vue';
  import type { ComponentPublicInstance } from 'vue';

  import { analyticsEventsLib } from '@/shared/lib';

  import { pricesLib, columnLib } from '../lib';
  import GraphColumn from './GraphColumn.vue';
  import {
    GraphDirectionEnum,
    type GraphDirection,
    type MappedRoundTripNearestSearch,
  } from '../types';

  const props = defineProps<{
    direction: GraphDirection;
    searches: MappedRoundTripNearestSearch[];
  }>();

  const emit = defineEmits<{ onActiveDateChange: [id: number] }>();

  const columnRef = ref<Array<ComponentPublicInstance>>();

  const maxPrice = computed(() => pricesLib.getMaxPrice(props.searches));

  const viewportWidth = document.documentElement.clientWidth;
  let lastScrollLeft = 0;
  let isProgrammaticScroll = false;
  let scrollDirection: 'right' | 'left' | null = null;
  let scrollEndTimer: ReturnType<typeof setTimeout> | null = null;

  const scrollToActiveColumn = () => {
    isProgrammaticScroll = true;
    nextTick(() => {
      columnRef.value
        ?.find(search =>
          search.$el.classList.contains(`activeColumn${props.direction}`),
        )
        ?.$el.scrollIntoView({ inline: 'center' });
    });
  };

  const changeActiveColumnByScroll = (event: Event) => {
    const target = event.target as HTMLElement;
    const currentScrollLeft = target.scrollLeft;

    if (!isProgrammaticScroll) {
      if (currentScrollLeft > lastScrollLeft) {
        scrollDirection = 'right';
      } else if (currentScrollLeft < lastScrollLeft) {
        scrollDirection = 'left';
      }

      if (scrollEndTimer) clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => {
        const direction =
          props.direction === GraphDirectionEnum.TO ? 'from' : 'to';
        if (scrollDirection === 'right') {
          analyticsEventsLib.priceSwipeRight(direction);
        } else if (scrollDirection === 'left') {
          analyticsEventsLib.priceSwipeLeft(direction);
        }
        scrollDirection = null;
      }, 150);
    } else {
      isProgrammaticScroll = false;
    }
    lastScrollLeft = currentScrollLeft;

    const middleElement = columnLib.findCenterElement(
      viewportWidth,
      props.direction,
    );
    if (middleElement) {
      emit('onActiveDateChange', Number(middleElement?.id));
    }
  };

  const changeActiveColumnByClick = (id: number) => {
    emit('onActiveDateChange', id);
    scrollToActiveColumn();
  };

  onMounted(() => {
    scrollToActiveColumn();
  });

  watch(
    () => props.searches.length,
    () => {
      if (props.direction === GraphDirectionEnum.BACK) {
        scrollToActiveColumn();
      }
    },
  );
</script>
